import { ErrorHandler, Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SeasonSettingsService } from '@coin/shared/season-mgmt/data-access';
import { GenericSeasonSettings, PositionEvaluationSetting, Season } from '@coin/shared/season-mgmt/util';
import { UserState } from '@coin/shared/auth/feature';
import { LoadingService } from '@coin/shared/core/data-access';
import { DisplayedCurrency, SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { ListViewTagFilterParameter, PaginatedResult } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { FilterSortState } from '@coin/customer/shared/filter-sort-bar-data-access';
import { MeritSupportSortingParameter } from '../../merit/merit-support/enums/merit-support-sorting-parameter.enum';
import {
  MercerEvaluationMember,
  MercerEvaluationMemberDetails,
  PositionSearchSlim
} from '../../shared/models/mercer-support-evaluation-member.model';
import { MercerService } from '../../shared/services/mercer.service';
import { MercerDirectReportsFilter } from '../enums';

const STATE_DEFAULTS: MercerStateModel = {
  evaluationMembers: [],
  seasons: [],
  season: undefined,
  filterParameter: [],
  currentPage: 0,
  maxPage: 0,
  sortingParameter: MeritSupportSortingParameter.Default,
  selectedCurrency: DisplayedCurrency.Local,
  seasonSettings: undefined,
  directReportsFilter: MercerDirectReportsFilter.DirectReports
};

const DEFAULT_PAGE_SIZE = 15;

interface MercerStateModel {
  evaluationMembers: MercerEvaluationMember[];
  seasons: Season[];
  season: Season;
  filterParameter: ListViewTagFilterParameter[];
  currentPage: number;
  maxPage: number;
  sortingParameter: string;
  selectedCurrency: DisplayedCurrency;
  seasonSettings: PositionEvaluationSetting;
  directReportsFilter: MercerDirectReportsFilter;
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class MercerState {
  private readonly userState = inject(UserState);
  private readonly filterSortState = inject(FilterSortState);
  private readonly seasonSettingsService = inject(SeasonSettingsService);
  private readonly mercerService = inject(MercerService);
  private readonly loadingService = inject(LoadingService);
  private readonly errorHandler = inject(ErrorHandler);

  public loadMercerEvaluationMembersSuccessful$ = new Subject<void>();

  private readonly mercerStateModel = signal<MercerStateModel>(STATE_DEFAULTS);

  public evaluationMembers = computed(() => this.mercerStateModel().evaluationMembers);
  public filterParameter = computed(() => this.mercerStateModel().filterParameter);
  public seasons = computed(() => this.mercerStateModel().seasons);
  public season = computed(() => this.mercerStateModel().season);
  public selectedCurrency = computed(() => this.mercerStateModel().selectedCurrency);
  public readonly selectedCurrencyChange$ = toObservable(this.selectedCurrency);
  public seasonSettings = computed(() => this.mercerStateModel().seasonSettings);
  public directReportsFilter = computed(() => this.mercerStateModel().directReportsFilter);
  public isLastPage = computed(() => this.mercerStateModel().currentPage >= this.mercerStateModel().maxPage);
  public queryText = computed(() => this.getFilterParameterText(this.mercerStateModel(), false));

  private getSortingParameterText(state: MercerStateModel): string {
    return state.sortingParameter !== MeritSupportSortingParameter.Default
      ? `&Sorting.Property=${state.sortingParameter}`
      : '';
  }

  private getFilterParameterText(state: MercerStateModel, showQueryPrefix = true): string {
    let filterText = '';

    state.filterParameter.forEach(param => {
      filterText += `&${showQueryPrefix ? 'Query.' : ''}${encodeURIComponent(param.category)}=${encodeURIComponent(param.value)}`;
    });

    if (state.directReportsFilter === MercerDirectReportsFilter.DirectReports) {
      filterText += `&Query.validationManagerId=${this.userState.user()?.id}`;
    }

    return filterText ?? '';
  }

  loadMercerEvaluationMembers(): Observable<PaginatedResult<MercerEvaluationMember>> {
    const seasonId = this.mercerStateModel().season.id;
    let filterText = this.filterSortState.getSortAndFilterText();

    filterText +=
      this.getFilterParameterText(this.mercerStateModel()) + this.getSortingParameterText(this.mercerStateModel());

    return this.mercerService.getEvaluationMembers(seasonId, 0, DEFAULT_PAGE_SIZE, filterText).pipe(
      this.loadingService.withLoadingScreen,
      tap(result => {
        this.mercerStateModel.update(currentState =>
          produce(currentState, state => {
            state.evaluationMembers = result.content;
            state.currentPage = 0;
            state.maxPage = result.pageCount - 1;
          })
        );
      }),
      tap(() => this.loadMercerEvaluationMembersSuccessful$.next())
    );
  }

  lazyLoadMercerEvaluationMembers(): Observable<PaginatedResult<MercerEvaluationMember>> {
    const seasonId = this.mercerStateModel().season.id;
    const { currentPage, maxPage } = this.mercerStateModel();

    if (currentPage >= maxPage) {
      // ngxs compatibility: a handler returning undefined still completed the dispatch
      return of(undefined);
    }
    let filterText = this.filterSortState.getSortAndFilterText();
    filterText +=
      this.getFilterParameterText(this.mercerStateModel()) + this.getSortingParameterText(this.mercerStateModel());
    return this.mercerService.getEvaluationMembers(seasonId, currentPage + 1, DEFAULT_PAGE_SIZE, filterText).pipe(
      this.loadingService.withLoadingScreen,
      tap(result => {
        this.mercerStateModel.update(currentState =>
          produce(currentState, state => {
            const evaluationMembers = [...state.evaluationMembers, ...result.content];

            state.evaluationMembers = evaluationMembers?.filter(
              (evaluationMember, index) => evaluationMembers.findIndex(e => e.id === evaluationMember.id) === index
            ); // delete all duplicates
            state.currentPage += 1;
          })
        );
      })
    );
  }

  setEvaluationMemberBySearch(payload: PositionSearchSlim): Observable<MercerEvaluationMemberDetails> {
    const seasonId = this.mercerStateModel().season.id;
    return this.mercerService.getEvaluationMemberDetails(seasonId, payload.evaluationMemberId).pipe(
      tap(newEvaluationMember => {
        this.mercerStateModel.update(currentState =>
          produce(currentState, state => {
            const evaluationMembersWithoutSearchedEmployee = state.evaluationMembers.filter(
              evaluationMember => evaluationMember.id !== newEvaluationMember.id
            );
            state.evaluationMembers = [newEvaluationMember, ...evaluationMembersWithoutSearchedEmployee];
          })
        );
      })
    );
  }

  setMercerEvaluationMember(payload: MercerEvaluationMember): void {
    this.mercerStateModel.update(currentState =>
      produce(currentState, state => {
        const index = state.evaluationMembers?.findIndex(member => payload.id === member.id);
        state.evaluationMembers[index] = payload;
      })
    );
  }

  loadMercerSeasons(managerEndDateAt?: string): Observable<void> {
    return this.mercerService.getSeasons(managerEndDateAt).pipe(
      tap(seasons => this.mercerStateModel.update(state => ({ ...state, seasons }))),
      filter(seasons => seasons?.length === 1),
      switchMap(seasons => this.setMercerSeason(seasons[0]))
    );
  }

  setMercerSeason(season: Season): Observable<void> {
    return this.loadMercerSeasonSettings({
      id: season.id,
      type: SeasonTypeEnum.PositionEvaluation
    }).pipe(
      map(() => undefined),
      tap(() => this.mercerStateModel.update(state => ({ ...state, season })))
    );
  }

  resetMercerState(): void {
    this.mercerStateModel.update(state => ({ ...state, ...STATE_DEFAULTS }));
  }

  setMercerCurrency(currency: DisplayedCurrency): void {
    this.mercerStateModel.update(state => ({ ...state, selectedCurrency: currency }));
  }

  addMercerFilterParameter(payload: ListViewTagFilterParameter): void {
    this.mercerStateModel.update(currentState =>
      produce(currentState, state => {
        state.filterParameter.push(payload);
      })
    );
  }

  setMercerFilterParameters(payload: ListViewTagFilterParameter[]): void {
    this.mercerStateModel.update(state => ({ ...state, filterParameter: payload }));
  }

  clearMercerFilterParameterKey(category: string): void {
    this.mercerStateModel.update(currentState =>
      produce(currentState, state => {
        state.filterParameter = state.filterParameter.filter(param => param.category !== category);
      })
    );
  }

  loadMercerSeasonSettings(payload: { id: string; type: SeasonTypeEnum }): Observable<GenericSeasonSettings> {
    return this.seasonSettingsService
      .getSeasonSettings(payload.id, payload.type)
      .pipe(
        tap((seasonSettings: PositionEvaluationSetting) =>
          this.mercerStateModel.update(state => ({ ...state, seasonSettings }))
        )
      );
  }

  setMercerDirectReportsFilter(payload: MercerDirectReportsFilter): void {
    this.mercerStateModel.update(state => ({ ...state, directReportsFilter: payload }));
  }

  promoteMercerMember(evaluationMemberId: string): Observable<unknown> {
    const { season } = this.mercerStateModel();
    return this.mercerService
      .updateEvaluationMemberDetails(season?.id, { id: evaluationMemberId, wasPromoted: true })
      .pipe(map(member => this.setMercerEvaluationMemberDetached(member)));
  }

  undoPromoteMercerMember(evaluationMemberId: string): Observable<unknown> {
    const { season } = this.mercerStateModel();
    return this.mercerService
      .updateEvaluationMemberDetails(season?.id, { id: evaluationMemberId, wasPromoted: false })
      .pipe(map(member => this.setMercerEvaluationMemberDetached(member)));
  }

  // ngxs compatibility: errors of the dispatched action only reached the ErrorHandler and never ended the calling stream
  private setMercerEvaluationMemberDetached(payload: MercerEvaluationMember): void {
    try {
      this.setMercerEvaluationMember(payload);
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }
}
